'use client'

import * as React from 'react'
import { Dialog as DialogPrimitive, VisuallyHidden } from 'radix-ui'

/**
 * Accessible-name fallback for DialogContent and SheetContent.
 *
 * Radix checks for a Title and a Description in a passive effect after the
 * first commit and logs a console warning when one is missing. The fallback
 * must therefore be in the DOM in that first commit, and must not render when
 * the caller gives its own title or description (a duplicate title id).
 *
 * The caller's DialogTitle / DialogDescription register a slot in two ways:
 *   1. A render-phase flag. The fallback renders after `children`, so React has
 *      already rendered the caller's subtree and the flag is set in the same pass.
 *      Setting it twice (StrictMode) is harmless.
 *   2. A layout-effect mount count, the steady-state truth for titles that
 *      appear or disappear after the first commit.
 * The render flag counts only until the fallback's first commit. From then on
 * only mounted titles count, so a stale flag (a caller title that re-rendered by
 * itself, or a discarded render) cannot keep the fallback away after it unmounts.
 */
type Slot = `title` | `description`

class DialogA11yStore {
  private rendered: Record<Slot, boolean> = { title: false, description: false }
  private mounted: Record<Slot, number> = { title: 0, description: 0 }
  private committed = false
  private listeners = new Set<() => void>()

  markRendered(slot: Slot) {
    this.rendered[slot] = true
  }

  markCommitted() {
    this.committed = true
  }

  has(slot: Slot) {
    return (!this.committed && this.rendered[slot]) || this.mounted[slot] > 0
  }

  mount(slot: Slot) {
    this.mounted[slot] += 1
    this.listeners.forEach((listener) => listener())
    return () => {
      this.mounted[slot] -= 1
      this.listeners.forEach((listener) => listener())
    }
  }

  subscribe = (listener: () => void) => {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }
}

const DialogA11yContext = React.createContext<DialogA11yStore | null>(null)

// Wraps the content of DialogContent / SheetContent. Must sit inside the Radix Content.
function DialogA11y({ children }: { children: React.ReactNode }) {
  const store = React.useMemo(() => new DialogA11yStore(), [])
  return (
    <DialogA11yContext.Provider value={store}>
      {children}
      <DialogA11yFallback store={store} />
    </DialogA11yContext.Provider>
  )
}

// No-op outside DialogA11y (e.g. a DialogTitle under a plain Radix Content).
function useDialogA11ySlot(slot: Slot) {
  const store = React.useContext(DialogA11yContext)
  store?.markRendered(slot)
  React.useLayoutEffect(() => store?.mount(slot), [store, slot])
}

function DialogA11yFallback({ store }: { store: DialogA11yStore }) {
  const hasTitle = React.useSyncExternalStore(store.subscribe, () => store.has(`title`))
  const hasDescription = React.useSyncExternalStore(store.subscribe, () => store.has(`description`))

  React.useLayoutEffect(() => store.markCommitted(), [store])

  return (
    <>
      {!hasTitle && (
        <VisuallyHidden.Root asChild>
          <DialogPrimitive.Title>Dialog</DialogPrimitive.Title>
        </VisuallyHidden.Root>
      )}
      {!hasDescription && (
        <VisuallyHidden.Root asChild>
          <DialogPrimitive.Description>Dialog content</DialogPrimitive.Description>
        </VisuallyHidden.Root>
      )}
    </>
  )
}

export { DialogA11y, useDialogA11ySlot }
